import React, { useEffect, useState } from "react";
import {
  Drawer,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogContent,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IOSSwitch } from "./IOSSwitch";
import FileUploader from "../../../components/sidebarAddVehicle/FileUploader";
import { useTranslation } from "react-i18next";
import { driverDrawer } from "../style";
import { LoopRounded } from "@mui/icons-material";
import { useTripDocument } from "../../../context/TripDocumentContext";
import { toast } from "react-toastify";
import { SendTripDocumentRequest } from "../../../types/api";
import {
  VectorDeclarationBufferIds,
  VectorDeclarationFetchMetaData,
  VehicleDocumentBufferIds,
  VehicleDocumentFetchMetaData,
} from "../../tripDetails/docPayloads";
import { useBusinessProfile } from "../../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../../utils/hooks/useAuth";
import {
  fleetService,
  VehicleDataPayload,
  VehicleDataPayloadUpdate,
} from "../../../api/fleetService/fleetServicesRegistered";
import VehicleDatePicker from "../../../components/sidebarAddVehicle/VehicleDatePicker";
import { useWindowSize } from "../../../utils/hooks/useWindowSize";
import CustomPopover from "./CustomPopover";
import { useNavigate } from "react-router-dom";
import TraceabilityPopover from '../../../components/TraceabilityPopover';

interface SidebarAddVehicleProps {
  open: boolean;
  onClose: () => void;
  onAddVehicle: (
    newVehicle: any,
    isEdit: boolean,
    originalLicensePlate: string
  ) => void;
  selectedVehicle?: any;
  fleetInfo: {
    vehicleTypes: Record<string, string[]>;
    envClasses: string[];
    fuels: string[];
  };
  handleOpenDialog: (licensePlate: string) => void;
}

type VehicleData = {
  licensePlate: string;
  licensePlate_lock: boolean;
  mainCategory: string;
  mainCategory_lock: boolean;
  category: string;
  category_lock: boolean;
  firstRegistrationDate: string | null;
  firstRegistrationDate_lock: boolean;
  environmentClass: string | null;
  environmentClass_lock: boolean;
  fuelType: string | null;
  fuelType_lock: boolean;
  emissionsData: number | null;
  emissionsData_lock: boolean;
  emissionsDataUOM: string;
  status: "ACTIVE" | "CANCELLED";
  lastTraceabilityCheck: string | null;
  lastUpdate: string;
  loadCapacity: number | null;
  loadCapacity_lock: boolean;
  loadCapacityUOM: string;
  source: "VLM" | "EAM";
  vehicleDocument: any;
  vectorDeclaration: any;
  traceability: any;
};

const SidebarAddVehicle: React.FC<SidebarAddVehicleProps> = ({
  open,
  onClose,
  onAddVehicle,
  selectedVehicle,
  fleetInfo,
  handleOpenDialog,
}) => {
  const navigate = useNavigate();
  //CONTEXT
  const { businessProfile } = useBusinessProfile();
  const { uploadPOD, sendTripDocument, fetchLatestPODDoc } = useTripDocument();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { getDocumentDetails, isDocumentLoading } = useTripDocument();

  //FILTER DATA
  const mainCategoryOptions = fleetInfo.vehicleTypes;
  const environmentClassOptions: Record<string, string[]> = {
    EURO: fleetInfo.envClasses,
  };
  const fuelTypeOptions: Record<string, string[]> = {
    CARBURANTE: fleetInfo.fuels,
  };

  //ERROR MANAGEMENT
  const [errors, setErrors] = useState({
    licensePlate: false,
    mainCategory: false,
    category: false,
    firstRegistrationDate: false,
    environmentClass: false,
    fuelType: false,
    emissionsData: false,
    vehicleDocument: false,
  });

  const validateFields = () => {
    const newErrors = {
      licensePlate: !vehicleData.licensePlate,
      mainCategory: !vehicleData.mainCategory,
      category: !vehicleData.category || vehicleData.category === "INCOMPLETE",
      firstRegistrationDate: !vehicleData.firstRegistrationDate,
      environmentClass:
        vehicleData.mainCategory === "Vehicle" && !vehicleData.environmentClass,
      fuelType: vehicleData.mainCategory === "Vehicle" && !vehicleData.fuelType,
      emissionsData: false,
      vehicleDocument: !files.vehicleDocument && !vehicleDocument,
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean); // Ritorna true se tutti i campi sono validi
  };

  //DATA INITIALIZATION
  const [vehicleData, setVehicleData] = useState<VehicleData>({
    licensePlate: selectedVehicle?.licensePlate || "",
    licensePlate_lock: selectedVehicle?.licensePlate_lock || false,
    mainCategory: selectedVehicle?.mainCategory || "",
    mainCategory_lock: selectedVehicle?.mainCategory_lock || false,
    category: selectedVehicle?.category || "",
    category_lock: selectedVehicle?.category_lock || false,
    firstRegistrationDate: selectedVehicle?.firstRegistrationDate || "",
    firstRegistrationDate_lock:
      selectedVehicle?.firstRegistrationDate_lock || false,
    environmentClass: selectedVehicle?.environmentClass || null,
    environmentClass_lock: selectedVehicle?.environmentClass_lock || false,
    fuelType: selectedVehicle?.fuelType || null,
    fuelType_lock: selectedVehicle?.fuelType_lock || false,
    emissionsData: selectedVehicle?.emissionsData || null,
    emissionsData_lock: selectedVehicle?.emissionsData_lock || false,
    emissionsDataUOM: selectedVehicle?.emissionsDataUOM || "g CO2/km",
    status: selectedVehicle?.status ?? "ACTIVE",
    lastTraceabilityCheck: selectedVehicle?.lastTraceabilityCheck || "",
    lastUpdate: selectedVehicle?.lastUpdate || "",
    loadCapacity: selectedVehicle?.loadCapacity || null,
    loadCapacity_lock: selectedVehicle?.loadCapacity_lock || false,
    loadCapacityUOM: selectedVehicle?.loadCapacityUOM || "q",
    source: selectedVehicle?.source === "EAM" ? "EAM" : "VLM",
    vehicleDocument: selectedVehicle?.vehicleDocument || null,
    vectorDeclaration: selectedVehicle?.vectorDeclaration || null,
    traceability: selectedVehicle?.traceability || null,
  });
  const [addVehicleLoader, setAddVehicleLoader] = useState<boolean>(false);
  const [files, setFiles] = useState<{ [key: string]: File | null }>({
    vehicleDocument: null,
    vectorDeclaration: null,
  });
  const [originalLicensePlate, setOriginalLicensePlate] = useState(
    selectedVehicle?.licensePlate || ""
  );
  const [vehicleDocument, setVehicleDocument] = useState<any>(null);
  const [vectorDeclaration, setVectorDeclaration] = useState<any>(null);
  const [traceabilityDialogOpen, setTraceabilityDialogOpen] = useState(false);
  const [traceabilityData, setTraceabilityData] = useState<{
    tripsInfo: Array<{ tmsObjectNumber: string; tripID: number }>;
    platform: string;
    licensePlate: string;
  }>({ tripsInfo: [], platform: "", licensePlate: "" });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  //GENERIC FUNCTIONS
  const handleChange = (field: string, value: any) => {
    const isTrailer = field === "mainCategory" && value === "Trailer";
    const isEditMode = !!selectedVehicle;
    const isEAMSource = selectedVehicle?.source === "EAM";

    // Se siamo in modalità modifica e la source è EAM, controlla i lock fields
    if (isEditMode && isEAMSource) {
      const lockField = `${field}_lock`;
      if (
        lockField in vehicleData &&
        vehicleData[lockField as keyof typeof vehicleData]
      ) {
        return; // Non permettere modifiche se il campo è bloccato
      }
    }

    setVehicleData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "mainCategory"
        ? {
            category: "", // Reset category
            environmentClass: isTrailer ? null : prev.environmentClass,
            fuelType: isTrailer ? null : prev.fuelType,
            emissionsData: isTrailer ? null : prev.emissionsData,
            emissionsDataUOM: isTrailer ? "g CO2/km" : prev.emissionsDataUOM,
            loadCapacity: isTrailer ? null : prev.loadCapacity,
            loadCapacityUOM: isTrailer ? "q" : prev.loadCapacityUOM,
          }
        : {}),
    }));
  };

  const handleSwitchChange = () => {
    setVehicleData((prev) => ({
      ...prev,
      status: prev.status === "ACTIVE" ? "CANCELLED" : "ACTIVE", // Alterna tra "ACTIVE" e "CANCELLED"
    }));
  };

  //DOCUMENTS MANAGEMENTS
  const handleFileUpload = (file: File | null, name: string) => {
    setFiles((prevFiles) => ({
      ...prevFiles,
      [name]: file,
    }));
  };

  const handleUpload = async (): Promise<boolean> => {

    

    let uploadSuccess = true;
    try {
      if (files.vehicleDocument) {
      const responseVehicle = await uploadPOD(files.vehicleDocument);
      if (
        responseVehicle &&
        Array.isArray(responseVehicle) &&
        responseVehicle.length > 0
      ) {
        const dynamicPayloadVehicle: SendTripDocumentRequest = {
          ...VehicleDocumentBufferIds,
          document: {
            bufferIds: responseVehicle,
          },
          fields: [
            ...VehicleDocumentBufferIds.fields.filter(
              (field) =>
                ![
                  "DocName",
                  "Originale",
                  "TESTO200_111",
                  "TESTO248_131",
                  "TESTO426_3",
                  "TABLE130_74",
                ].includes(field.name)
            ),
            {
              value: files.vehicleDocument?.name || "",
              name: "DocName",
              className: "SubjectFieldDTO",
            },
            {
              value: files.vehicleDocument?.name || "",
              name: "Originale",
              className: "OriginalFieldDTO",
            },
            {
              value: vehicleData.licensePlate, //TARGA
              name: "TESTO200_111",
              externalId: "TARGA",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: businessProfile.legacycodes[0].legacycode, //LEGACYCODE
              name: "TESTO248_131",
              externalId: "SUPPLIER_EXTERNAL_CODE_STR",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: businessProfile.accountcode, //ACCOUNT ID CE
              name: "TESTO426_3",
              externalId: "SUPPLIER_CODE",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: businessProfile.name, //RAGIONE SOCIALE
              name: "TABLE130_74",
              externalId: "SUPPLIER_COMPANY_NAME",
              className: "AdditionalFieldComboDTO",
            },
          ],
        };

        await sendTripDocument(dynamicPayloadVehicle);
      } else {
        toast.error("Errore durante l'invio del documento");
        uploadSuccess = false; // Upload fallito
      }}
      if (files.vectorDeclaration) {
        const responseVector = await uploadPOD(files.vectorDeclaration);
        if (
          responseVector &&
          Array.isArray(responseVector) &&
          responseVector.length > 0
        ) {
          const dynamicPayloadVector: SendTripDocumentRequest = {
            ...VectorDeclarationBufferIds,
            document: { bufferIds: responseVector },
            fields: [
              ...VectorDeclarationBufferIds.fields.filter(
                (field) =>
                  ![
                    "DocName",
                    "Originale",
                    "TESTO200_111",
                    "TESTO248_131",
                    "TESTO426_3",
                    "TABLE130_74",
                  ].includes(field.name)
              ),
              {
                value: files.vectorDeclaration.name || "",
                name: "DocName",
                className: "SubjectFieldDTO",
              },
              {
                value: files.vectorDeclaration.name || "",
                name: "Originale",
                className: "OriginalFieldDTO",
              },
              {
                value: vehicleData.licensePlate,
                name: "TESTO200_111",
                externalId: "TARGA",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: businessProfile.legacycodes[0].legacycode,
                name: "TESTO248_131",
                externalId: "SUPPLIER_EXTERNAL_CODE_STR",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: businessProfile.accountcode,
                name: "TESTO426_3",
                externalId: "SUPPLIER_CODE",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: businessProfile.name,
                name: "TABLE130_74",
                externalId: "SUPPLIER_COMPANY_NAME",
                className: "AdditionalFieldComboDTO",
              },
            ],
          };

          await sendTripDocument(dynamicPayloadVector);
        } else {
          toast.error(
            "Errore durante l'invio del documento 'vectorDeclaration'"
          );
          uploadSuccess = false; // Non blocchiamo il processo, ma segnaliamo l'errore
        }
      }
      return uploadSuccess;
    } catch (error) {
      toast.error("Errore durante il caricamento dei file");
      return false;
    }
  };

  const fetchVehicleDocuments = async () => {
    try {
      const dynamicPayload = {
        ...VehicleDocumentFetchMetaData,
        searchFilterDtoList:
          VehicleDocumentFetchMetaData.searchFilterDtoList.map((filter) => ({
            ...filter,
            fields: [
              ...filter.fields.filter(
                (field) =>
                  ![
                    "TESTO200_111",
                    "TESTO426_3",
                    "TESTO248_131",
                    "TABLE130_74",
                  ].includes(field.name)
              ),
              {
                operator: 1,
                valore1: null,
                multiple: selectedVehicle.licensePlate, //mettere qui la targa
                name: "TESTO200_111",
                externalId: "TARGA",
                description: "TARGA",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                valore1: null,
                multiple: businessProfile.accountcode, //mettere qui il account id CE
                name: "TESTO426_3",
                externalId: "SUPPLIER_CODE",
                description: "Codice Anagrafica ERP",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                multiple: businessProfile.legacycodes[0].legacycode, //legacy code
                name: "TESTO248_131",
                externalId: "SUPPLIER_EXTERNAL_CODE_STR",
                description: "Codice Anagrafica TMS (String)",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                multiple: businessProfile.name,
                name: "TABLE130_74",
                externalId: "SUPPLIER_COMPANY_NAME",
                description: "Denominazione Fornitore",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
            ],
          })),
      };

      const response = await fetchLatestPODDoc(dynamicPayload);
      if (!response || !Array.isArray(response)) {
        return;
      }
      // Filtra solo i documenti con CREATION_DATE non null e valida la data
      const validDocs = response.filter((doc) => doc.CREATION_DATE !== null);

      // Se non ci sono documenti validi, interrompi il flusso
      if (validDocs.length === 0) {
        return;
      }

      // Trova il documento con la CREATION_DATE più recente
      const latestDocument = validDocs.reduce((latest, doc) => {
        return new Date(doc.CREATION_DATE!) > new Date(latest.CREATION_DATE!)
          ? doc
          : latest;
      }, validDocs[0]); // Inizializza con il primo documento valido

      setVehicleDocument([latestDocument]);
    } catch (err: any) {
      console.log(err.message || "Errore durante il recupero dei dati.");
    }
  };

  const fetchVectorDeclaration = async () => {
    try {
      const dynamicPayload = {
        ...VectorDeclarationFetchMetaData,
        searchFilterDtoList:
          VectorDeclarationFetchMetaData.searchFilterDtoList.map((filter) => ({
            ...filter,
            fields: [
              ...filter.fields.filter(
                (field) =>
                  ![
                    "TESTO200_111",
                    "TESTO426_3",
                    "TESTO248_131",
                    "TABLE130_74",
                  ].includes(field.name)
              ),
              {
                operator: 1,
                valore1: null,
                multiple: selectedVehicle.licensePlate, //mettere qui la targa
                name: "TESTO200_111",
                externalId: "TARGA",
                description: "TARGA",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                valore1: null,
                multiple: businessProfile.accountcode, //mettere qui il account id CE
                name: "TESTO426_3",
                externalId: "SUPPLIER_CODE",
                description: "Codice Anagrafica ERP",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                multiple: businessProfile.legacycodes[0].legacycode, //legacy code
                name: "TESTO248_131",
                externalId: "SUPPLIER_EXTERNAL_CODE_STR",
                description: "Codice Anagrafica TMS (String)",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
              {
                operator: 0,
                multiple: businessProfile.name,
                name: "TABLE130_74",
                externalId: "SUPPLIER_COMPANY_NAME",
                description: "Denominazione Fornitore",
                className: "FieldBaseForSearchStringDto",
                isAdditional: true,
                visible: true,
              },
            ],
          })),
      };

      const response = await fetchLatestPODDoc(dynamicPayload);
      if (!response || !Array.isArray(response)) {
        console.log("La risposta API non è valida.");
        return;
      }

      // Filtra i documenti con CREATION_DATE non null
      const validDocs = response.filter((doc) => doc.CREATION_DATE !== null);

      // Se non ci sono documenti validi, interrompi il flusso
      if (validDocs.length === 0) {
        console.log("Nessun documento valido trovato.");
        return;
      }

      // Trova il documento con la CREATION_DATE più recente
      const latestDocument = validDocs.reduce((latest, doc) => {
        return new Date(doc.CREATION_DATE!) > new Date(latest.CREATION_DATE!)
          ? doc
          : latest;
      }, validDocs[0]); // Inizializza con il primo documento valido

      // Imposta solo il documento più recente
      setVectorDeclaration([latestDocument]);
    } catch (err: any) {
      console.log(err.message || "Errore durante il recupero dei dati.");
    }
  };

  const handleOpenFilePreview = async (
    documentId: string,
    fileFormat: string
  ) => {
    try {
      // Mappa delle estensioni ai tipi MIME
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
      };

      // Determina il tipo MIME in base al formato, con fallback
      const contentType =
        mimeTypes[fileFormat.toLowerCase()] || "application/octet-stream";

      // Recupera i dettagli del documento come ArrayBuffer
      const documentDetails = await getDocumentDetails(documentId);

      // Creazione del Blob con l'ArrayBuffer
      const fileBlob = new Blob([documentDetails], { type: contentType });

      // Creazione dell'URL e apertura in una nuova finestra
      const url = URL.createObjectURL(fileBlob);
      window.open(url, "_blank");
    } catch (error) {
      console.error(
        "Errore durante il recupero o la visualizzazione del documento:",
        error
      );
    }
  };

  const handleInsertVehicle = async (): Promise<boolean> => {
    try {
      // Controllo per azzerare i campi se la categoria è "Trailer"
      const isTrailer = vehicleData.mainCategory === "Trailer";

      // Creazione payload per il veicolo
      const vehiclePayload: VehicleDataPayload = {
        legacyCodes: {
          legacyCode: businessProfile.legacycodes[0].legacycode,
          company: businessProfile.legacycodes[0].company,
        },
        licensePlate: vehicleData.licensePlate,
        vehicleType: vehicleData.mainCategory,
        vehicleSubType: vehicleData.category,
        environmentClass: isTrailer
          ? undefined
          : vehicleData.environmentClass || undefined,
        fuelType: isTrailer ? undefined : vehicleData.fuelType || undefined,
        firstRegistrationDate: vehicleData.firstRegistrationDate || undefined,
        emissionsData: isTrailer
          ? undefined
          : vehicleData.emissionsData || undefined,
        emissionsDataUOM: isTrailer ? "" : vehicleData.emissionsDataUOM,
        user: user?.email || "",
        status: vehicleData.status,
        loadCapacity: vehicleData.loadCapacity || 0,
        loadCapacityUOM: vehicleData.loadCapacityUOM,
        VatNumber: businessProfile.vatnr ?? "-",
        source: "VLM", // Sempre VLM indipendentemente dal valore in vehicleData.source
      };

      // Creazione payload per l'aggiornamento del veicolo
      const baseUpdatePayload = {
        legacyCodes: {
          legacyCode: businessProfile.legacycodes[0].legacycode,
          company: businessProfile.legacycodes[0].company,
        },
        licensePlate: vehicleData.licensePlate,
        vehicleSubType:
          vehicleData.source === "EAM" ? vehicleData.category : undefined,
        user: user?.email || "",
        source: "VLM", // Sempre VLM
        carrierTMSLocation: null,
      };

      // Aggiungiamo solo i campi sbloccati o sempre modificabili
      const vehiclePayloadUpdate: VehicleDataPayloadUpdate = {
        ...baseUpdatePayload,
        // Campi con il loro lock status
        ...(isTrailer || !vehicleData.environmentClass_lock
          ? {
              environmentClass: isTrailer
                ? undefined
                : vehicleData.environmentClass || undefined,
            }
          : {}),
        ...(isTrailer || !vehicleData.fuelType_lock
          ? {
              fuelType: isTrailer
                ? undefined
                : vehicleData.fuelType || undefined,
            }
          : {}),
        ...(isTrailer ||
        vehicleData.source === "VLM" ||
        (vehicleData.source === "EAM" && vehicleData.firstRegistrationDate_lock)
          ? {}
          : {
              firstRegistrationDate: vehicleData.firstRegistrationDate
                ? vehicleData.firstRegistrationDate // Già in formato ISO
                : undefined,
            }),
        ...(isTrailer || !vehicleData.emissionsData_lock
          ? {
              emissionsData: isTrailer
                ? undefined
                : vehicleData.emissionsData || undefined,
            }
          : {}),
        // Campi sempre modificabili
        emissionsDataUOM: isTrailer ? "" : vehicleData.emissionsDataUOM,
        loadCapacity: vehicleData.loadCapacity || 0,
        loadCapacityUOM: vehicleData.loadCapacityUOM,
        // Altri campi obbligatori
        VatNumber: businessProfile.vatnr ?? "-",
      };

      if (!selectedVehicle || !selectedVehicle.licensePlate) {
        // Chiamata API per inserire il veicolo
        const insertResponse = await fleetService.insertVehicle(vehiclePayload);
        if (!insertResponse) {
          toast.error(
            t("screens.trips.tripDocuments.insertDocumentError")
          );
          return false;
        }
        
        // Check traceability response
        if (insertResponse.isTraceability === false || (insertResponse.isTraceability === null && insertResponse.traceabilitySuccess === false)) {
          setTraceabilityData({ 
            tripsInfo: insertResponse.tripsInfo || [], 
            platform: insertResponse.platform || "",
            licensePlate: vehicleData.licensePlate 
          });
          setTraceabilityDialogOpen(true);
          setAnchorEl(document.body);
        }

        // Update vehicle data with lastTraceabilityCheckDate
        vehicleData.lastTraceabilityCheck = insertResponse.lastTraceabilityCheckDate || null;
        vehicleData.traceability = insertResponse.isTraceability || null;
      } else {
        // Chiamata API per aggiornare il veicolo
        const updateResponse = await fleetService.updateVehicle(
          vehiclePayloadUpdate
        );
        if (!updateResponse) {
          toast.error(
            t("screens.trips.tripDocuments.updateDocumentError")
          );
          return false;
        }

        // Check traceability response
        if (updateResponse.isTraceability === false || (updateResponse.isTraceability === null && updateResponse.traceabilitySuccess === false)) {
          setTraceabilityData({ 
            tripsInfo: updateResponse.tripsInfo || [], 
            platform: updateResponse.platform || "",
            licensePlate: vehicleData.licensePlate 
          });
          setTraceabilityDialogOpen(true);
          setAnchorEl(document.body);
        }

        // Update vehicle data with lastTraceabilityCheckDate
        vehicleData.lastTraceabilityCheck = updateResponse.lastTraceabilityCheckDate || null;
        vehicleData.traceability = updateResponse.isTraceability || null;
      }

      if(selectedVehicle){
        toast.success("Veicolo aggiornato con successo!");
      }else{
        toast.success("Veicolo inserito con successo!");
      }
      return true;
    } catch (error) {
      if(selectedVehicle){
        toast.error(t("screens.trips.tripDocuments.updateDocumentError"));
      }else{
        toast.error(  t("screens.trips.tripDocuments.insertDocumentError"));
      }
      return false;
    }
  };
  const handleSubmit = async () => {
    if (!validateFields() || !businessProfile) return;

    try {
      setAddVehicleLoader(true);
      let uploadSuccess = true;
 
      if (
        selectedVehicle?.vehicleDocument?.name?.trim() !== files.vehicleDocument?.name?.trim() || 
        selectedVehicle?.vectorDeclaration?.name?.trim() !== files.vectorDeclaration?.name?.trim()
      ) {
        uploadSuccess = await handleUpload();
        if (!uploadSuccess) {
          console.error("Upload fallito, interruzione del processo.");
          setAddVehicleLoader(false);
          return;
        }
      } else {
        console.log("Documenti già presenti, skippando l'upload.");
      }

      const insertSuccess = await handleInsertVehicle();
      if (!insertSuccess) {
        console.error("Inserimento veicolo fallito, interruzione del processo.");
        setAddVehicleLoader(false);
        return;
      }

      const newVehicle = {
        ...vehicleData,
        vehicleDocument: files.vehicleDocument,
        vectorDeclaration: files.vectorDeclaration,
      };

      onAddVehicle(newVehicle, !!selectedVehicle, originalLicensePlate);
      setErrors({
        licensePlate: false,
        mainCategory: false,
        category: false,
        firstRegistrationDate: false,
        environmentClass: false,
        fuelType: false,
        emissionsData: false,
        vehicleDocument: false,
      });
      
      // Chiudi la sidebar solo dopo che tutte le operazioni sono completate con successo
      onClose();
      setAddVehicleLoader(false);
    } catch (error) {
      console.error("Errore durante l'upload:", error);
      setAddVehicleLoader(false);
    }
  };
  useEffect(() => {}, [isDocumentLoading]);
  //USE EFFECTS
  useEffect(() => {
    if (selectedVehicle) {
      setVehicleData({
        licensePlate: selectedVehicle?.licensePlate || "",
        licensePlate_lock: selectedVehicle?.licensePlate_lock || false,
        mainCategory: selectedVehicle?.mainCategory || "",
        mainCategory_lock: selectedVehicle?.mainCategory_lock || false,
        category: selectedVehicle?.category || "",
        category_lock: selectedVehicle?.category_lock || false,
        firstRegistrationDate: selectedVehicle?.firstRegistrationDate || "",
        firstRegistrationDate_lock:
          selectedVehicle?.firstRegistrationDate_lock || false,
        environmentClass: selectedVehicle?.environmentClass || null,
        environmentClass_lock: selectedVehicle?.environmentClass_lock || false,
        fuelType: selectedVehicle?.fuelType || null,
        fuelType_lock: selectedVehicle?.fuelType_lock || false,
        emissionsData: selectedVehicle?.emissionsData || null,
        emissionsData_lock: selectedVehicle?.emissionsData_lock || false,
        emissionsDataUOM: selectedVehicle?.emissionsDataUOM || "g CO2/km",
        status: selectedVehicle.status ?? "ACTIVE",
        lastTraceabilityCheck: selectedVehicle?.lastTraceabilityCheck || "",
        lastUpdate: selectedVehicle?.lastUpdate || "",
        loadCapacity: selectedVehicle?.loadCapacity || null,
        loadCapacity_lock: selectedVehicle?.loadCapacity_lock || false,
        loadCapacityUOM: selectedVehicle?.loadCapacityUOM || "q",
        source: selectedVehicle.source === "EAM" ? "EAM" : "VLM",
        vehicleDocument: selectedVehicle?.vehicleDocument || null,
        vectorDeclaration: selectedVehicle?.vectorDeclaration || null,
        traceability: selectedVehicle?.traceability || null,
      });
      setOriginalLicensePlate(selectedVehicle?.licensePlate || "");
      setFiles({
        vehicleDocument: selectedVehicle.vehicleDocument || null,
        vectorDeclaration: selectedVehicle.vectorDeclaration || null,
      });
    } else {
      // Resetta i dati se si sta aggiungendo un nuovo veicolo
      setVehicleData({
        licensePlate: "",
        licensePlate_lock: false,
        mainCategory: "",
        mainCategory_lock: false,
        category: "",
        category_lock: false,
        firstRegistrationDate: "",
        firstRegistrationDate_lock: false,
        environmentClass: null,
        environmentClass_lock: false,
        fuelType: null,
        fuelType_lock: false,
        emissionsData: null,
        emissionsData_lock: false,
        emissionsDataUOM: "g CO2/km",
        status: "ACTIVE",
        lastTraceabilityCheck: "",
        lastUpdate: "",
        loadCapacity: null,
        loadCapacity_lock: false,
        loadCapacityUOM: "q",
        source: "VLM",
        vehicleDocument: null,
        vectorDeclaration: null,
        traceability: null,
      });

      setFiles({
        vehicleDocument: null,
        vectorDeclaration: null,
      });
      setErrors({
        licensePlate: false,
        mainCategory: false,
        category: false,
        firstRegistrationDate: false,
        environmentClass: false,
        fuelType: false,
        emissionsData: false,
        vehicleDocument: false,
      });
    }
  }, [selectedVehicle, open]);

  useEffect(() => {
    if (!open) {
      setVehicleDocument(null);
      setVectorDeclaration(null);
    } else {
      fetchVehicleDocuments();
      fetchVectorDeclaration();
    }
  }, [open]);

  useEffect(() => {}, [vehicleData]);

  const isFieldDisabled = (field: string) => {
    if (!selectedVehicle) return false;
    if (selectedVehicle.source === "VLM") return true;
    if (selectedVehicle.source === "EAM") {
      const lockField = `${field}_lock`;
      return vehicleData[lockField as keyof typeof vehicleData] === true;
    }
    return false;
  };

  
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 700;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        elevation={5}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "transparent" },
          },
        }}
      >
      
      <Box sx={{...driverDrawer.wrapper,width:isMobile? "100vw": 520,}}>
          <Box sx={{...driverDrawer.headerWrapper,padding:isMobile? "24px 8px": "32px 24px",}}>
            <Typography sx={driverDrawer.headerTitle}>
              {selectedVehicle && selectedVehicle.licensePlate
                ? t("common.sidebars.addVehicle.modify")
                : t("common.sidebars.addVehicle.heading")}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#333333" }} />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{...driverDrawer.headerSubtitleWrapper,padding:isMobile? "8px" : "16px 24px",}}>
            <Typography sx={driverDrawer.headerSubtitle}>
              {t("common.sidebars.addVehicle.paragraph")}
            </Typography>
            <Box sx={{...driverDrawer.plateCategoryWrapper,flexDirection:isMobile? 'column': "row",rowGap:isMobile? "8px":0}}>
              {/**Vehicle Plate */}
              <Box sx={driverDrawer.generalBox}>
                <Typography sx={driverDrawer.mainText}>
                  {t("common.sidebars.addVehicle.vehiclePlate")} *
                </Typography>
                <TextField
                  fullWidth
                  placeholder={
                    t("common.sidebars.addVehicle.addVehiclePlate") || ""
                  }
                  variant="outlined"
                  value={vehicleData.licensePlate}
                  onChange={(e) => handleChange("licensePlate", e.target.value)}
                  error={errors.licensePlate}
                  helperText={
                    errors.licensePlate
                      ? t("common.sidebars.addVehicle.mandatoryField")
                      : ""
                  }
                  sx={driverDrawer.textField}
                  disabled={!!selectedVehicle}
                />
              </Box>

              {/**Fleet Type */}
              <Box sx={driverDrawer.generalBox}>
                <Typography sx={driverDrawer.mainText}>
                  {t("common.sidebars.addVehicle.fleetType")} *
                </Typography>
                <FormControl fullWidth error={!!errors.mainCategory}>
                  <Select
                    displayEmpty
                    renderValue={(selected) =>
                      selected
                        ? t(
                            `screens.fleetAndRoute.vehicles.${selected}`,
                            selected
                          ).toString()
                        : t("common.sidebars.addVehicle.addFleetType")
                    }
                    sx={driverDrawer.selectComponent}
                    value={vehicleData?.mainCategory || ""}
                    onChange={(e) => handleChange("mainCategory", e.target.value)}
                    disabled={!!selectedVehicle}
                  >
                    <MenuItem disabled value="">
                      {t("common.sidebars.addVehicle.addFleetType")}
                    </MenuItem>
                    {mainCategoryOptions &&
                      Object.keys(mainCategoryOptions).length > 0 &&
                      Object.keys(mainCategoryOptions).map((category) => {
                        return (
                          <MenuItem key={category} value={category}>
                            {t(
                              `screens.fleetAndRoute.vehicles.${category}`,
                              category
                            ).toString()}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/**Category & RegistrationDate*/}
            <Box sx={{...driverDrawer.plateCategoryWrapper,flexDirection:isMobile? 'column': "row",rowGap:isMobile? "8px":0}}>
              <Box sx={driverDrawer.generalBox}>
                <Typography sx={driverDrawer.mainText}>
                  {t("common.sidebars.addVehicle.categoryType")} *
                </Typography>
                <FormControl
                  error={!!errors.category}
                  fullWidth
                  disabled={
                    selectedVehicle
                      ? selectedVehicle.source === "VLM"
                        ? true
                        : false
                      : false
                  }
                >
                  <Select
                    displayEmpty
                    renderValue={(selected) =>
                      selected
                        ? t(
                            `screens.fleetAndRoute.vehicles.${selected}`,
                            selected
                          ).toString()
                        : t("common.sidebars.addVehicle.addCategoryType")
                    }
                    value={
                      vehicleData?.category === "INCOMPLETE"
                        ? ""
                        : vehicleData?.category || ""
                    }
                    onChange={(e) => handleChange("category", e.target.value)}
                    sx={driverDrawer.selectComponent}
                    error={errors.category}
                  >
                    <MenuItem disabled value="">
                      {t("common.sidebars.addVehicle.addCategoryType")}
                    </MenuItem>
                    {mainCategoryOptions?.[vehicleData?.mainCategory] &&
                      Array.isArray(
                        mainCategoryOptions[vehicleData.mainCategory]
                      ) &&
                      mainCategoryOptions[vehicleData.mainCategory].map(
                        (cat: any) => (
                          <MenuItem key={cat} value={cat}>
                            {t(
                              `screens.fleetAndRoute.vehicles.${cat}`,
                              cat
                            ).toString()}
                          </MenuItem>
                        )
                      )}
                  </Select>
                  {errors.category && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#CF0A2C",
                        margin: 0,
                        padding: 0,
                        marginTop: "4px",
                      }}
                    >
                      {t("common.sidebars.addVehicle.mandatoryField")}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={driverDrawer.generalBox}>
                <Typography sx={driverDrawer.mainText}>
                  {t("common.sidebars.addVehicle.registrationDate")} *
                </Typography>
                <VehicleDatePicker
                  vehicleData={{
                    firstRegistrationDate:
                      vehicleData.firstRegistrationDate || undefined,
                    firstRegistrationDate_lock:
                      vehicleData.firstRegistrationDate_lock,
                  }}
                  handleChange={handleChange}
                  errors={errors}
                  selectedVehicle={selectedVehicle}
                  disabled={
                    selectedVehicle
                      ? selectedVehicle.source === "VLM"
                        ? true
                        : vehicleData.firstRegistrationDate_lock
                      : false
                  }
                />
                {errors.firstRegistrationDate && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#CF0A2C",
                      margin: 0,
                      padding: 0,
                      marginTop: "4px",
                    }}
                  >
                    {t("common.sidebars.addVehicle.mandatoryField")}
                  </Typography>
                )}
              </Box>
            </Box>

            {/**Vehicle Document */}
            <Box sx={driverDrawer.generalBox}>
              <Typography sx={driverDrawer.mainText}>
                {t("common.sidebars.addVehicle.vehicleDocument")} *
              </Typography>
              {isDocumentLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <CircularProgress size={24} sx={{ color: "#CF0A2C" }} />
                </Box>
              ) : (
                <>
                  {!vehicleData.vehicleDocument &&
                    vehicleDocument &&
                    vehicleDocument.map((doc: any, index: any) => {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                              textDecoration: "underline",
                              color: "#CF0A2C",
                            }}
                            onClick={() =>
                              handleOpenFilePreview(
                                String(doc?.DOCNUMBER || ""),
                                String(doc?.FORMATO || "")
                              )
                            }
                            key={index}
                          >
                            {doc.ORIGINALE}
                          </Typography>
                          <IconButton onClick={() => setVehicleDocument(null)}>
                            <LoopRounded sx={{ color: "#CF0A2C" }} />
                          </IconButton>
                        </Box>
                      );
                    })}
                  {vehicleData.vehicleDocument ? (
                    <Box sx={driverDrawer.documentBox}>
                      <a
                        href={
                          typeof vehicleData.vehicleDocument === "string"
                            ? vehicleData.vehicleDocument
                            : URL.createObjectURL(vehicleData.vehicleDocument)
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={driverDrawer.uploadBox}
                      >
                        {typeof vehicleData.vehicleDocument === "string"
                          ? vehicleData.vehicleDocument.split("/").pop() // Estrai il nome file dall'URL
                          : vehicleData.vehicleDocument.name}{" "}
                        {/* Usa il nome se è un File */}
                      </a>

                      <IconButton
                        onClick={() => {
                          handleChange("vehicleDocument", null);
                          setVehicleDocument(null);
                        }}
                      >
                        <LoopRounded sx={{ color: "#CF0A2C" }} />
                      </IconButton>
                    </Box>
                  ) : (
                    !vehicleDocument && (
                      <FileUploader
                        error={errors.vehicleDocument ? true : false}
                        name="vehicleDocument"
                        onFileUpload={handleFileUpload}
                        acceptedTypes={[
                          "application/pdf",
                          "image/png",
                          "image/jpeg",
                        ]}
                      />
                    )
                  )}
                  {errors.vehicleDocument && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#CF0A2C",
                        margin: 0,
                        padding: 0,
                        marginTop: "4px",
                      }}
                    >
                      {t("common.sidebars.addVehicle.mandatoryField")}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            {/**Load Capacity */}
            {vehicleData.category !== "Standard Truck" &&
              vehicleData.category !== "Three axles truck" &&
              vehicleData.category !== "Mega Truck" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    columnGap: "16px",
                  }}
                >
                  <Box sx={driverDrawer.generalBox}>
                    <Typography sx={driverDrawer.mainText}>
                      {t("common.sidebars.addVehicle.loadCapacity")}
                    </Typography>
                    <div className="relative " style={{ width:isMobile? "100%": "50%" }}>
                      <TextField
                        fullWidth
                        placeholder={
                          t("common.sidebars.addVehicle.loadCapacity") || ""
                        }
                        variant="outlined"
                        type="number"
                        value={vehicleData.loadCapacity}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, "");
                          if (value.length > 11) {
                            value = value.slice(0, 11);
                          }
                          handleChange(
                            "loadCapacity",
                            value ? Math.max(0, parseInt(value, 10)) : ""
                          );
                        }}
                        onKeyDown={(e) => {
                          if (
                            !/^\d$/.test(e.key) && // Consenti solo numeri
                            e.key !== "Backspace" &&
                            e.key !== "Delete" &&
                            e.key !== "ArrowLeft" && // Consenti freccia sinistra
                            e.key !== "ArrowRight" // Consenti freccia destra
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={
                          (e) =>
                            e.target instanceof HTMLElement && e.target.blur() // Disabilita incremento con la rotella
                        }
                        sx={{ ...driverDrawer.textField, width: "100%" }}
                        disabled={
                          selectedVehicle
                            ? selectedVehicle.source === "VLM"
                              ? false
                              : vehicleData.loadCapacity_lock
                            : false
                        }
                      />

                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 text-sm bg-white px-1">
                        q
                      </span>
                    </div>
                  </Box>
                </Box>
              )}
            <Divider />
            {/**Sustenibility Data */}
            {vehicleData.mainCategory !== "Trailer" && (
              <>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: "700", color: "#333333" }}
                >
                  {t("common.sidebars.addVehicle.sustainabilityData")}
                </Typography>
                <Box sx={{...driverDrawer.plateCategoryWrapper,flexDirection:isMobile? 'column': "row",rowGap:isMobile? "8px":0}}>
                  <Box sx={driverDrawer.generalBox}>
                    <Typography sx={driverDrawer.mainText}>
                      {t("common.sidebars.addVehicle.environmentalClassEuro")} *
                    </Typography>
                    <FormControl fullWidth error={errors.environmentClass}>
                      <Select
                        displayEmpty
                        renderValue={(selected) =>
                          selected
                            ? selected
                            : t("common.sidebars.addVehicle.sustainabilityData")
                        }
                        sx={driverDrawer.selectComponent}
                        value={vehicleData.environmentClass}
                        onChange={(e) =>
                          handleChange("environmentClass", e.target.value)
                        }
                        disabled={
                          selectedVehicle
                            ? selectedVehicle.source === "VLM"
                              ? false
                              : vehicleData.environmentClass_lock
                            : false
                        }
                      >
                        <MenuItem disabled value="">
                          {t("common.sidebars.addVehicle.sustainabilityData")}
                        </MenuItem>
                        {environmentClassOptions &&
                          Object.keys(environmentClassOptions).length > 0 &&
                          Object.keys(environmentClassOptions).map((category) =>
                            Array.isArray(environmentClassOptions[category])
                              ? environmentClassOptions[category].map(
                                  (envClass) => (
                                    <MenuItem key={envClass} value={envClass}>
                                      {envClass}
                                    </MenuItem>
                                  )
                                )
                              : undefined
                          )}
                      </Select>
                      {errors.environmentClass && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#CF0A2C",
                            margin: 0,
                            padding: 0,
                            marginTop: "4px",
                          }}
                        >
                          {t("common.sidebars.addVehicle.mandatoryField")}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>

                  <Box sx={driverDrawer.generalBox}>
                    <Typography sx={driverDrawer.mainText}>
                      {t("common.sidebars.addVehicle.fuelType")} *
                    </Typography>
                    <FormControl fullWidth error={errors.fuelType}>
                      <Select
                        displayEmpty
                        renderValue={(selected) =>
                          selected
                            ? t(
                                `screens.fleetAndRoute.fuelType.${selected}`,
                                selected
                              ).toString()
                            : t("common.sidebars.addVehicle.addFuelType")
                        }
                        sx={driverDrawer.selectComponent}
                        value={vehicleData.fuelType}
                        onChange={(e) => handleChange("fuelType", e.target.value)}
                        disabled={
                          selectedVehicle
                            ? selectedVehicle.source === "VLM"
                              ? false
                              : vehicleData.fuelType_lock
                            : false
                        }
                      >
                        <MenuItem disabled value="">
                          {t("common.sidebars.addVehicle.addFuelType")}
                        </MenuItem>
                        {Object.keys(fuelTypeOptions).map((category) =>
                          fuelTypeOptions[category].map((fuel) => (
                            <MenuItem key={fuel} value={fuel}>
                              {t(
                                `screens.fleetAndRoute.fuelType.${fuel}`,
                                fuel
                              ).toString()}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {errors.fuelType && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#CF0A2C",
                            margin: 0,
                            padding: 0,
                            marginTop: "4px",
                          }}
                        >
                          {t("common.sidebars.addVehicle.mandatoryField")}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={driverDrawer.generalBox}>
                  <Typography sx={driverDrawer.mainText}>
                    {t("common.sidebars.addVehicle.emissionData")}
                  </Typography>
                  <div className="relative w-full">
                    <TextField
                      type="text"
                      sx={driverDrawer.textField}
                      fullWidth
                      placeholder={
                        t("common.sidebars.addVehicle.addEmissionData") || ""
                      }
                      variant="outlined"
                      value={vehicleData.emissionsData}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9,\\.]/g, "");
                        value = value.replace(",", ".");
                        if ((value.match(/\./g) || []).length > 1) {
                          return;
                        }
                        if (value.replace(".", "").length > 11) {
                          return;
                        }
                        handleChange("emissionsData", value);
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/[\d.,]/.test(e.key) && // Permette solo numeri, `.` e `,`
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" && // Consenti freccia sinistra
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault(); // Blocca input non validi
                        }
                      }}
                      onWheel={(e) =>
                        e.target instanceof HTMLElement && e.target.blur()
                      } // Disabilita incremento/decremento con la rotella
                      error={errors.emissionsData}
                      helperText={
                        errors.emissionsData
                          ? t("common.sidebars.addVehicle.mandatoryField")
                          : ""
                      }
                      disabled={
                        selectedVehicle
                          ? selectedVehicle.source === "VLM"
                            ? false
                            : vehicleData.emissionsData_lock
                          : false
                      }
                      slotProps={{
                        formHelperText: {
                          sx: {
                            fontSize: "12px",
                            color: "#CF0A2C",
                            margin: 0,
                            padding: 0,
                            marginTop: "4px",
                          },
                        },
                      }}
                    />

                    <span
                      style={{ top: errors.emissionsData ? 20 : "" }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 text-sm bg-white px-1"
                    >
                      g CO2/km
                    </span>
                  </div>
                </Box>

                <Box sx={driverDrawer.generalBox}>
                  <Typography sx={driverDrawer.mainText}>
                    {t("common.sidebars.addVehicle.vectoDeclaration")}
                  </Typography>
                  {isDocumentLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", my: 2 }}
                    >
                      <CircularProgress size={24} sx={{ color: "#CF0A2C" }} />
                    </Box>
                  ) : (
                    <>
                      {!vehicleData.vectorDeclaration &&
                        vectorDeclaration &&
                        vectorDeclaration.map((doc: any, index: any) => {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  textDecoration: "underline",
                                  color: "#CF0A2C",
                                }}
                                onClick={() =>
                                  handleOpenFilePreview(
                                    String(doc?.DOCNUMBER || ""),
                                    String(doc?.FORMATO || "")
                                  )
                                }
                                key={index}
                              >
                                {doc.ORIGINALE}
                              </Typography>
                              <IconButton
                                onClick={() => setVectorDeclaration(null)}
                              >
                                <LoopRounded sx={{ color: "#CF0A2C" }} />
                              </IconButton>
                            </Box>
                          );
                        })}

                      {vehicleData.vectorDeclaration ? (
                        <Box sx={driverDrawer.documentBox}>
                          <a
                            href={
                              typeof vehicleData.vectorDeclaration === "string"
                                ? vehicleData.vectorDeclaration
                                : URL.createObjectURL(
                                    vehicleData.vectorDeclaration
                                  )
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={driverDrawer.uploadBox}
                          >
                            {typeof vehicleData.vectorDeclaration === "string"
                              ? vehicleData.vectorDeclaration.split("/").pop() // Estrai il nome file dall'URL
                              : vehicleData.vectorDeclaration.name}{" "}
                            {/* Usa il nome se è un File */}
                          </a>

                          <IconButton
                            onClick={() => {
                              handleChange("vectorDeclaration", null);
                              setVectorDeclaration(null);
                            }}
                          >
                            <LoopRounded sx={{ color: "#CF0A2C" }} />
                          </IconButton>
                        </Box>
                      ) : (
                        !vectorDeclaration && (
                          <FileUploader
                            error={errors.vehicleDocument ? true : false}
                            name="vectorDeclaration"
                            onFileUpload={handleFileUpload}
                            acceptedTypes={[
                              "application/pdf",
                              "image/png",
                              "image/jpeg",
                            ]}
                          />
                        )
                      )}
                    </>
                  )}
                </Box>
              </>
            )}

            {/**Vehicle Status */}

            {!selectedVehicle?.licensePlate && (
              <Box sx={driverDrawer.statusBox}>
                <Typography sx={driverDrawer.mainText}>
                  {t("common.sidebars.addVehicle.vehicleStatus")}:
                </Typography>
                {selectedVehicle && selectedVehicle.licensePlate ? (
                  <IOSSwitch
                    checked={vehicleData.status === "ACTIVE"}
                    onChange={() => handleOpenDialog(vehicleData.licensePlate)}
                  />
                ) : (
                  <IOSSwitch
                    checked={vehicleData.status === "ACTIVE"}
                    onChange={() => handleSwitchChange()}
                  />
                )}
              </Box>
            )}
          </Box>
          <Divider />
          <Box
            sx={{
              width: "100%",
              backgroundColor: "white",
              zIndex: 200,
              height: "93px",
            }}

          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              sx={{...driverDrawer.button,  maxWidth:isMobile? "96%": "300px", right:isMobile? 8: "24px", }}
              disabled={addVehicleLoader}
            >
              {addVehicleLoader ? (
                <CircularProgress size={24} sx={{ color: "#ffffff" }} />
              ) : selectedVehicle ? (
                t("common.sidebars.saveButtonText")
              ) : (
                t("common.sidebars.addDriver.add")
              )}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Traceability Popover */}
   

      <TraceabilityPopover
        open={traceabilityDialogOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setTraceabilityDialogOpen(false);
          setAnchorEl(null);
        }}
        data={{
          licensePlate: traceabilityData.licensePlate,
          platform: traceabilityData.platform,
          tripsInfo: traceabilityData.tripsInfo
        }}
        useCustomPopover={true}
      />
      
    </>
  );
};

export default SidebarAddVehicle;
